import React, { useState, useEffect } from 'react';
import { Dialog, AppBar, Toolbar, IconButton, Typography, Button, Icon, List, ListItem, ListItemIcon, ListItemSecondaryAction, Checkbox, ListItemText, Collapse } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useSelector, useDispatch } from 'react-redux';
import { get, set, size, uniqBy } from 'global/lib/lodash';
import Alert from '@material-ui/lab/Alert';
import Image from 'material-ui-image';
import clsx from 'clsx';
import { getTaskStatus, getTaskImage, sortUsersAlphabetically } from 'global/utils';
import { Loader } from 'global/styles';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { updateTasksToLatest, setNewTasksToSent } from 'redux/actions/view';
import ReassignModal from './ReassignModal';
import useStyles from './NotifyModal.styles';
import { useUser } from 'global/hooks/useUser';

export default (props) => {
  const [error, setError] = useState();
  const [userMap, setUserMap] = useState({});
  const [assignees, setAssignees] = useState([]);
  const [reAssignUserList, reAssignUserListUpdate] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [reModal, _reModal] = useState({ open: false });
  const dispatch = useDispatch();

  const classes = useStyles();
  const { me } = useUser();
  // if an input realm is specified, use that to get assignee options
  // if not, use the current global realm of user
  const globalRealm = useSelector((state) => get(state, 'auth.realm.id'));
  const params = props.hasOwnProperty('realmId')
    ? {
        waitFor: props.realmId,
        params: { filter: { content_realm_id: props.realmId } },
        useCache: { reload: 1 },
      }
    : {
        waitFor: globalRealm,
        params: { filter: { content_realm_id: globalRealm ? globalRealm : get(me, 'realm.id') } },
        useCache: { reload: 1 },
      };
  const usersList = useRetrieveData({ url: '/users' }, { useEffect: true, build: ['users'], ...params });

  /* Commented this section of code to resolve memory leaks 
  window.ACT = () => {
    dispatch(undoSent());
  }; */

  useEffect(() => {
    if (props.open) {
      if (!size(props.unsentTasks.data)) {
        return handleClose();
      }

      reAssignUserListUpdate(uniqBy([me, ...(usersList.data || [])], 'id'));
      const _userMap = {
        // [id]: { user group
        //   tasks: task,
        //   user: user,
        //   selectedTasks: [],
        //   open: false,
        // }
      };

      const userGroups = get(props, 'unsentTasks.name.userGroups', {});
      Object.keys(userGroups).map((key) => {
        const group = userGroups[key];

        set(_userMap, [key], {
          ...group,
          selectedTasks: [],
          open: true,
          key,
        });
      });
      setAssignees(Object.keys(_userMap).map((key) => ({ ...get(_userMap, key) })));
      setUserMap(_userMap);
    }
  }, [props.open, props.unsentTasks]);

  const handleClose = () => {
    props.onClose();
  };

  const getSelectedTasks = () => {
    let tids = [];
    Object.keys(userMap).forEach((k) => {
      tids = [...tids, ...get(userMap, [k, 'selectedTasks'], [])];
    });
    return get(props, 'unsentTasks.data', []).filter((t) => tids.indexOf(t.id) > -1);
  };

  const reassign = async () => {
    const tasks = getSelectedTasks();

    _reModal({
      open: true,
      title: 'Re-Assign Tasks',
      tasks,
      assignees: sortUsersAlphabetically(reAssignUserList),
      onClose: async (update) => {
        try {
          setLoading(true);
          _reModal({ open: false });
          if (update) {
            await dispatch(updateTasksToLatest());
          }
          setLoading(false);
        } catch (err) {
          console.log(err);
          setLoading(false);
        }
      },
    });
  };

  const dontNotify = () => {
    const obj = {
      tasksSent: [],
    };

    Object.keys(userMap).forEach((k) => {
      const selTasks = get(userMap, [k, 'selectedTasks']);
      if (size(selTasks)) {
        obj.tasksSent = [...obj.tasksSent, ...selTasks];
      }
    });

    props.actions.dontNotify(obj.tasksSent);
  };

  const stopProp = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const share = () => {
    const selectedTasks = getSelectedTasks();
    get(props, 'actions.share', () => 0)(selectedTasks);
  };

  const notify = async () => {
    if (sending) return;
    setSending(true);
    const obj = {
      users: [],
      tasksSent: [],
      emails: 0,
      tasks: 0,
    };

    Object.keys(userMap).forEach((k) => {
      const selTasks = get(userMap, [k, 'selectedTasks']);
      if (size(selTasks)) {
        obj.users = [...obj.users, ...get(userMap, [k, 'assignees'], [])];
        obj.emails += 1;
        obj.tasksSent = [...obj.tasksSent, ...selTasks];
        obj.tasks += size(selTasks);
      }
    });

    const getName = () => {
      const others = size(obj.users) - 1;
      return `${get(obj, ['users', 0, 'name'])}` + (size(obj.users) > 1 ? ` and ${others} ${others > 1 ? 'others' : 'other'}` : '');
    };

    const messages = await dispatch(setNewTasksToSent(obj.tasksSent));

    setSending(false);

    props.actions.setAlert({
      message: `${size(messages)} emails of ${obj.tasks} tasks sent to ${getName()}.`,
    });
  };

  const handleSelectAll = () => {
    const meta = getMeta();
    const uMap = { ...userMap };
    if (meta.allSelected) {
      for (let k in uMap) {
        set(uMap, [k, 'selectedTasks'], []);
      }
    } else {
      for (let k in uMap) {
        set(
          uMap,
          [k, 'selectedTasks'],
          get(uMap, [k, 'tasks'], []).map((t) => t.id)
        );
      }
    }
    setUserMap(uMap);
  };

  const handleToggle = (e, item) => {
    stopProp(e);

    const uMap = { ...userMap };
    const obj = get(uMap, item.key);

    if (size(obj.selectedTasks) >= size(obj.tasks)) {
      obj.selectedTasks = [];
    } else {
      obj.selectedTasks = obj.tasks.map((t) => t.id);
    }
    setUserMap(uMap);
  };

  const handleToggleOpen = (e, item) => {
    stopProp(e);

    const id = get(item, 'key');
    const uMap = { ...userMap };
    const obj = get(uMap, id);
    set(obj, 'open', get(obj, 'open') ? false : true);
    setUserMap(uMap);
  };

  const toggleTaskSelect = (e, item, task) => {
    stopProp(e);
    const uMap = { ...userMap };

    const tasks = get(uMap, [item.key, 'selectedTasks'], []);
    const taskIndex = tasks.indexOf(task.id);
    if (taskIndex > -1) {
      tasks.splice(taskIndex, 1);
    } else {
      tasks.push(task.id);
    }
    set(uMap, [item.key, 'selectedTasks'], tasks);
    setUserMap(uMap);
  };

  const getMeta = () => {
    const obj = {
      somethingSelected: false,
      allSelected: true,
      meSelected: false,
    };

    for (let k in userMap) {
      const uobj = userMap[k];

      const selTasks = get(uobj, 'selectedTasks', []);
      if (size(selTasks)) {
        if (get(uobj, 'key') == me.id) {
          obj.meSelected = true;
        }
        obj.somethingSelected = true;
        if (size(selTasks) < size(uobj.tasks)) {
          obj.allSelected = false;
        }
      } else {
        obj.allSelected = false;
      }
    }
    return obj;
  };

  const meta = getMeta();
  const somethingSelected = meta.somethingSelected;

  return (
    <Dialog className={classes.dialog} fullScreen disableEscapeKeyDown open={props.open} onClose={handleClose}>
      <AppBar color="secondary" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </IconButton>

          <div className={classes.barTitle}>
            <Typography variant="h5" className={classes.title}>
              New Task Notifications
            </Typography>
          </div>
        </Toolbar>
      </AppBar>

      <div className={classes.container}>
        {error && (
          <Alert
            severity="error"
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setError();
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        <List className={classes.list}>
          <ListItem key={'all'} role={undefined} dense button className={classes.selectAll} onClick={handleSelectAll}>
            <ListItemIcon classes={{ root: classes.checkbox }}>
              <Checkbox edge="start" checked={meta.allSelected} />
            </ListItemIcon>
            <ListItemText primary={meta.allSelected ? 'Deselect All' : `Select All`} />
          </ListItem>
          {assignees.map((item, i) => {
            const { key, tasks } = item;
            const id = key;
            const obj = get(userMap, id);
            const isOpen = get(obj, 'open');
            const taskCount = size(obj.tasks);
            const selectedTaskCount = size(obj.selectedTasks);
            const indeterminate = selectedTaskCount && selectedTaskCount < taskCount ? true : false;
            return (
              <>
                <ListItem key={id} role={undefined} button onClick={(e) => handleToggle(e, item)}>
                  <ListItemIcon classes={{ root: classes.checkbox }}>
                    <Checkbox edge="start" indeterminate={indeterminate} checked={!!selectedTaskCount} />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.assigneeText}
                    primary={`Assigned To: ${item.assignees
                      .map((a) => {
                        return a.id == me.id ? 'Me' : a.name;
                      })
                      .join(', ')}`}
                  />
                  <ListItemSecondaryAction classes={{ root: classes.secondaryAction }}>
                    <Button endIcon={<Icon>{isOpen ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>} onClick={(e) => handleToggleOpen(e, item)} size="small" className={classes.secondaryButton}>
                      {`${size(item.tasks)} Tasks`}
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>

                <Collapse in={isOpen} unmountOnExit>
                  <List disablePadding className={classes.childList}>
                    {(tasks || []).map((task, i) => {
                      const sel = obj.selectedTasks.indexOf(task.id) > -1;
                      const image = getTaskImage(task);
                      return (
                        <ListItem className={classes.listItem} key={task.id} dense button onClick={(e) => toggleTaskSelect(e, item, task)}>
                          <ListItemIcon classes={{ root: classes.checkbox }}>
                            <Checkbox edge="start" checked={sel} />
                          </ListItemIcon>
                          <ListItemText primary={get(task, 'name', '')} secondary={getTaskStatus(task).title} />
                          {image.src && (
                            <ListItemSecondaryAction>
                              <div>
                                <Image src={image.src} style={{ width: 60 }} />
                                {image.icon && <Icon classes={{ root: classes.imageIcon }}>{image.icon}</Icon>}
                              </div>
                            </ListItemSecondaryAction>
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </>
            );
          })}
        </List>

        <div className={classes.buttonWrap}>
          <Button
            onClick={reassign}
            startIcon={loading ? <Loader size={16} classes={{ svg: classes.whiteIcon }} /> : <Icon>person_add</Icon>}
            className={clsx(classes.button, classes.share)}
            variant="contained"
            disabled={loading || !somethingSelected}
          >
            RE-ASSIGN
          </Button>
          <div className={clsx(classes.row, classes.buttonContainer)}>
            <Button variant="contained" startIcon={<Icon>cancel</Icon>} onClick={dontNotify} className={clsx(classes.button, classes.dontNotify)} disabled={!somethingSelected}>
              {`DON'T NOTIFY`}
            </Button>

            <Button variant="contained" startIcon={<Icon>share</Icon>} onClick={share} className={clsx(classes.button, classes.share)} disabled={!somethingSelected}>
              {`SHARE PDF`}
            </Button>

            <Button
              variant="contained"
              startIcon={<Icon>{sending ? <Loader size={20} classes={{ svg: classes.whiteIcon }} /> : 'send'}</Icon>}
              onClick={notify}
              className={clsx(classes.button, classes.notify)}
              disabled={!somethingSelected || meta.meSelected}
            >
              {`NOTIFY ASSIGNEES`}
            </Button>
          </div>
        </div>
      </div>

      <ReassignModal {...reModal} />
    </Dialog>
  );
};
